import React, {useEffect, useRef, useState} from "react";
import {SOCCER_GOAL_SHOT_MAP_SCALE} from "../../../app/consts";
import {ChartEventViewModel} from "../../../../domain/full-game/models/chart-events/event/chart-event.view-model";
import {ChartEventShotViewModel} from "../../../../domain/full-game/models/chart-events/event/chart-event.shot.view-model";

export const ShotMapGoalViewComposer = (Widget) => ({
                                                      controller,
                                                      game,
                                                      events,
                                                      selectedShot,
                                                      isPlayerPopup,
                                                      onChange,
                                                      forceBigScale,
                                                      forceAllShotsActive,
                                                      shouldRenderEventDetails = true
                                                    }) => {
  if (!events || events?.length === 0) {
    return null;
  }

  const eventsVM = events?.map(event => new ChartEventViewModel(event, event.sortIndex, event.isRtl, event.isGameContext))
  const ref = useRef();
  const [containerDimensions, setContainerDimensions] = useState(0);

  const maxBigScaleHeight = 60;
  const minBigScaleRight = 40.7;
  const minBigScaleLeft = 59.3;

  useEffect(() => {
    if (ref.current) {
      setContainerDimensions({
        height: ref.current.getBoundingClientRect().width / windowRatio,
        width: ref.current.getBoundingClientRect().width
      })
    }
  }, [])

  const calcGoalScale = (y, z) => {
    let scale: SOCCER_GOAL_SHOT_MAP_SCALE;
    if (y === null || z === null || forceBigScale || selectedEvent.isBlockedShot) {
      scale = SOCCER_GOAL_SHOT_MAP_SCALE.big;
    } else if (y >= minBigScaleRight && y <= minBigScaleLeft && z < maxBigScaleHeight) {
      scale = SOCCER_GOAL_SHOT_MAP_SCALE.big;
    } else {
      scale = SOCCER_GOAL_SHOT_MAP_SCALE.small;
    }
    return scale;
  }

  const segmentsFractionsX = [0.164, 0.482, 0.5, 0.883, 1.3];
  const segmentsFractionsY = [0.48, 0.96, 1, 1.386, 2.036];
  const smallScaleGoalWidthFraction = 1 / (2 * segmentsFractionsX[segmentsFractionsX.length - 1]);
  const bigScaleGoalWidthFraction = 1 / (2 * segmentsFractionsX[segmentsFractionsX.length - 2]);
  const selectedEvent = selectedShot?.key ? eventsVM.find(event => event.key === selectedShot.key) : eventsVM.first() ?? eventsVM.first();

  let scaleType = selectedEvent.outcome ? calcGoalScale(selectedEvent.outcome?.y, selectedEvent.outcome?.z) : null;
  const goalScale = scaleType === SOCCER_GOAL_SHOT_MAP_SCALE.big ? bigScaleGoalWidthFraction : smallScaleGoalWidthFraction;
  const segmentsDataX = [0.018, 0.048, 0.058, 0.093, 0.5];
  const segmentsDataY = [0.20, 0.38, 0.42, 0.6, 1];

  const windowRatio = 2.759;
  const goalRatio = 2.166;


  const getGoalShotPositionX = (ballWidth, percentX, targetGoalWidth, containerWidth) => {
    const xFromCenter = Math.abs(percentX - 0.5);
    const goalFractionX = getGoalFraction(xFromCenter, segmentsDataX, segmentsFractionsX);
    const centerX = containerWidth / 2;
    const buttonSize = ballWidth;
    let xPos = centerX - goalFractionX * targetGoalWidth - buttonSize / 2;
    if (percentX < 0.5) {
      xPos = containerWidth - xPos - buttonSize;
    }
    return xPos;
  }

  const getGoalShotPositionY = (ballWidth, percentY, targetGoalHeight) => {
    const goalFractionY = getGoalFraction(percentY, segmentsDataY, segmentsFractionsY);
    const bottomY = 0;
    let posY = bottomY - goalFractionY * targetGoalHeight + ballWidth / 2;

    return posY;
  }

  const getGoalFraction = (dataValue, segmentsData, segmentsFractions) => {
    for (let index = 0; index < segmentsData.length; index++) {
      const dataSegmentEnd = segmentsData[index];
      const dataSegmentStart = index === 0 ? 0 : segmentsData[index - 1];
      if (dataValue >= dataSegmentStart && dataValue <= dataSegmentEnd) {
        const segmentFractionStart = index === 0 ? 0 : segmentsFractions[index - 1];
        const dataFraction = (dataValue - dataSegmentStart) / (dataSegmentEnd - dataSegmentStart);
        return segmentFractionStart + dataFraction * (segmentsFractions[index] - segmentFractionStart);
      }
    }
    return 0;
  }

  const ballWidth = 14;

  const goalFrame = {
    width: containerDimensions.width * goalScale,
    height: (containerDimensions.width * goalScale) / goalRatio
  };

  const mapBigScaleEvent = (event) => {
    if (event.outcome?.z > maxBigScaleHeight) {
      event.outcome.z = maxBigScaleHeight;
    } if (event.outcome?.y < minBigScaleRight) {
      event.outcome.y = minBigScaleRight;
    } if (event.outcome?.y > minBigScaleLeft) {
      event.outcome.y = minBigScaleLeft;
    }

    return event;
  }

  const setEvents = (event) => {
    if (scaleType === SOCCER_GOAL_SHOT_MAP_SCALE.big) {
      event = mapBigScaleEvent(event)
    }

    const x: Number = event.outcome ? getGoalShotPositionX(ballWidth, event.outcome.y / 100, goalFrame.width, containerDimensions.width) : 0;
    const y: Number = event.outcome ? getGoalShotPositionY(ballWidth, event.outcome.z / 100, goalFrame.height) : 0;
    const isActive: Boolean = forceAllShotsActive ? true : event.key === selectedEvent.key

    return new ChartEventShotViewModel(event, x, y, isActive)
  }

  const newProps = {
    controller,
    containerHeight: containerDimensions.height,
    goalFrame,
    isPlayerPopup,
    game,
    onChange: (event) => onChange && onChange(event),
    selectedEvent,
    events: eventsVM.map((event: ChartEventViewModel) => setEvents(event)),
    shouldRenderEventDetails,
    forceAllShotsActive
  };

  return (
    <Widget {...newProps} ref={ref}/>
  );
};
